import { useState, useCallback } from 'react';
import { useAppDispatch } from '../app/hooks';
import { gqlClient } from '../components/AppProvider';
import { setToastConfig } from '../features/EDIContainer/EDIContainerSlice';
import { ToastSeverity } from '../util/Constants';
import { downloadDocument, getErrorMessage } from '../util/Common';
import { MutationDownloadBlogStorage } from '../gql/MutationExecuteBlobStorageDownload';
import { 
    BlobDownloadRequestInput, 
    BlobDocumentType, 
    BlobDocumentFormat,
    RequestResult 
} from '../gql-types.generated';

/**
 * Custom hook for downloading blob storage files
 * Handles the download mutation, parsing the response, and triggering the browser download
 * 
 * @returns Object containing downloadBlob function and isDownloading state
 */
export const useBlobStorageDownload = () => {
    const dispatch = useAppDispatch();
    const [isDownloading, setIsDownloading] = useState(false);

    /**
     * Downloads a blob storage file
     * 
     * @param blobDocumentId - The ID of the blob document to download
     * @param blobFileName - The name of the file (optional, used for the downloaded filename)
     * @param blobDocumentType - The type of blob document (e.g., TransactionMap, StandardExchangeFormatFile)
     * @param mimeType - Optional MIME type override. If not provided, will be inferred from file extension
     */
    const downloadBlob = useCallback(async (
        blobDocumentId: string,
        blobFileName: string | undefined,
        blobDocumentType: BlobDocumentType,
        mimeType?: string
    ) => {
        setIsDownloading(true);
        
        try {
            const downloadData: BlobDownloadRequestInput = {
                blobDocumentId,
                blobFileName,
                blobDocumentType,
                blobDocumentFormat: BlobDocumentFormat.Base64,
            };
            
            const downloadStatus = await MutationDownloadBlogStorage(gqlClient, downloadData);
            
            if (downloadStatus?.result === RequestResult.Success && downloadStatus.data) {
                // Parse the BlobDownloadResponse JSON string
                const parsedData = JSON.parse(downloadStatus.data);

                // Strip GUID prefix (e.g., "3681aea1-32b2-49fa-b44f-8edbfafb54bb__") from the blob file name for download
                const strippedGuidPrefixFileName = parsedData.BlobFileName.replace(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}__/, '');
                
                // Determine MIME type if not provided
                const finalMimeType = mimeType || (
                    strippedGuidPrefixFileName.toLowerCase().endsWith(".xml") 
                        ? "text/xml" 
                        : strippedGuidPrefixFileName.toLowerCase().endsWith(".xsl")
                        ? "text/xsl"
                        : "text/plain"
                );
                
                // Trigger the browser download
                downloadDocument(
                    parsedData.FileContent, 
                    strippedGuidPrefixFileName, 
                    finalMimeType, 
                    true
                );
            } else if (downloadStatus?.result === RequestResult.Fail) {
                dispatch(setToastConfig({
                    message: downloadStatus.message as string,
                    severity: ToastSeverity.Error
                }));
                getErrorMessage(
                    downloadStatus.message as string, 
                    downloadStatus.errors, 
                    true, 
                    true
                );
            }
        } catch (e) {
            dispatch(setToastConfig({
                message: (e as Error).message,
                severity: ToastSeverity.Error
            }));
        } finally {
            setIsDownloading(false);
        }
    }, [dispatch]);

    return { downloadBlob, isDownloading };
};